import React, {
  createContext,
  useState,
  useContext,
  useCallback,
  useEffect,
  useRef,
} from 'react';
import axiosInstance from 'axiosInstance';
import baseUrl from 'Base_Url/baseUrl';
import { useToast } from '@chakra-ui/react';

const RegionDataContext = createContext({});

export const useRegionData = () => useContext(RegionDataContext);

const useDebounce = (callback, delay, key) => {
  const timerRef = useRef({});

  return useCallback(
    (...args) => {
      if (timerRef.current[key]) {
        clearTimeout(timerRef.current[key]);
      }

      timerRef.current[key] = setTimeout(() => {
        callback(...args);
      }, delay);
    },
    [callback, delay, key],
  );
};

export const RegionDataProvider = ({ children }) => {
  const [tahsils, setTahsils] = useState([]);
  const [circles, setCircles] = useState([]);
  const [subdivisions, setSubdivisions] = useState([]);
  const [villages, setVillages] = useState([]);

  const [isLoading, setIsLoading] = useState({
    tahsils: false,
    circles: false,
    subdivisions: false,
    villages: false,
  });

  const [counts, setCounts] = useState({
    tahsil: 0,
    mandal: 0,
    sajja: 0,
    village: 0,
    subdivision: 0,
  });

  const toast = useToast();

  const getToken = () => localStorage.getItem('token');

  const hasFetched = useRef({
    tahsils: false,
    circles: false,
    subdivisions: false,
  });

  const clearAllData = useCallback(() => {
    setTahsils([]);
    setCircles([]);
    setSubdivisions([]);
    setVillages([]);
    setCounts({
      tahsil: 0,
      mandal: 0,
      sajja: 0,
      village: 0,
      subdivision: 0,
    });
    hasFetched.current = {
      tahsils: false,
      circles: false,
      subdivisions: false,
    };
  }, []);

  const fetchTahsils = useCallback(
    async (search = '') => {
      const token = getToken();
      if (!token) {
        setTahsils([]);
        return [];
      }

      if (!hasFetched.current.tahsils && search === '') {
        hasFetched.current.tahsils = true;
      }

      setIsLoading((prev) => ({ ...prev, tahsils: true }));
      try {
        const res = await axiosInstance.get(`${baseUrl}/master/all-tahsils`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { search },
        });

        let tahsilArray = res.data?.data?.data || [];
        tahsilArray.sort((a, b) => a.name.localeCompare(b.name));
        setTahsils(tahsilArray);
        setCounts((prev) => ({ ...prev, tahsil: res.data?.data?.total || 0 }));
        return tahsilArray;
      } catch (err) {
        if (err.response?.status !== 401) {
          toast({
            title:
              err.response?.data?.message ||
              err.response?.data?.data ||
              'Error fetching tahsils',
            status: 'error',
            duration: 3000,
          });
        }
        return [];
      } finally {
        setIsLoading((prev) => ({ ...prev, tahsils: false }));
      }
    },
    [toast],
  );

  const fetchCircles = useCallback(
    async (search = '') => {
      const token = getToken();
      if (!token) {
        setCircles([]);
        return [];
      }

      if (!hasFetched.current.circles && search === '') {
        hasFetched.current.circles = true;
      }

      setIsLoading((prev) => ({ ...prev, circles: true }));
      try {
        const res = await axiosInstance.get(`${baseUrl}/master/all-circles`, {
          headers: { Authorization: `Bearer ${token}` },
          params: { search },
        });

        const circleArray = res.data?.data?.data || [];
        setCircles(circleArray);
        setCounts((prev) => ({ ...prev, mandal: res.data?.data?.total || 0 }));
        return circleArray;
      } catch (err) {
        if (err.response?.status !== 401) {
          toast({
            title:
              err.response?.data?.message ||
              err.response?.data?.data ||
              'Error fetching circles',
            status: 'error',
            duration: 3000,
          });
        }
        return [];
      } finally {
        setIsLoading((prev) => ({ ...prev, circles: false }));
      }
    },
    [toast],
  );

  const fetchSubdivisions = useCallback(
    async (search = '') => {
      const token = getToken();
      if (!token) {
        setSubdivisions([]);
        return [];
      }

      if (!hasFetched.current.subdivisions && search === '') {
        hasFetched.current.subdivisions = true;
      }

      setIsLoading((prev) => ({ ...prev, subdivisions: true }));
      try {
        const res = await axiosInstance.get(
          `${baseUrl}/master/all-subdivisions`,
          {
            headers: { Authorization: `Bearer ${token}` },
            params: { search },
          },
        );

        const responseData = res.data;
        let subdivisionsData = [];

        if (responseData?.data?.data) {
          subdivisionsData = responseData.data.data;
        } else if (responseData?.data) {
          subdivisionsData = responseData.data;
        } else if (Array.isArray(responseData)) {
          subdivisionsData = responseData;
        }

        setSubdivisions(
          Array.isArray(subdivisionsData) ? subdivisionsData : [],
        );
        setCounts((prev) => ({
          ...prev,
          sajja: responseData?.data?.total || 0,
        }));
        return subdivisionsData;
      } catch (err) {
        if (err.response?.status !== 401) {
          toast({
            title:
              err.response?.data?.message ||
              err.response?.data?.data ||
              'Error fetching subdivisions',
            status: 'error',
            duration: 3000,
          });
        }
        return [];
      } finally {
        setIsLoading((prev) => ({ ...prev, subdivisions: false }));
      }
    },
    [toast],
  );

  const fetchVillages = useCallback(
    async (search = '', tahsilId = '') => {
      const token = getToken();
      if (!token) {
        setVillages([]);
        return [];
      }

      setIsLoading((prev) => ({ ...prev, villages: true }));
      try {
        const params = { search: search || undefined };
        if (tahsilId) params.tahsilId = tahsilId;

        const res = await axiosInstance.get(`${baseUrl}/master/all-villages`, {
          headers: { Authorization: `Bearer ${token}` },
          params,
        });

        let villagesArray = res.data?.data?.data || [];
        setVillages(villagesArray);

        console.log('villagesArray.length', villagesArray.length);
        setCounts((prev) => ({ ...prev, village: res.data?.data?.total || 0 }));
        return villagesArray;
      } catch (err) {
        if (err.response?.status !== 401) {
          toast({
            title:
              err.response?.data?.message ||
              err.response?.data?.data ||
              'Error fetching villages',
            status: 'error',
            duration: 3000,
          });
        }
        return [];
      } finally {
        setIsLoading((prev) => ({ ...prev, villages: false }));
      }
    },
    [toast],
  );

  const searchTahsils = useDebounce(fetchTahsils, 500, 'tahsils');
  const searchCircles = useDebounce(fetchCircles, 500, 'circles');
  const searchSubdivisions = useDebounce(
    fetchSubdivisions,
    500,
    'subdivisions',
  );
  const searchVillages = useDebounce(fetchVillages, 500, 'villages');

  useEffect(() => {
    const initializeData = async () => {
      const token = getToken();
      if (!token) {
        clearAllData();
        return;
      }

      try {
        const fetchedTahsils = await fetchTahsils();

        let tahsilIdForVillages = '';
        if (fetchedTahsils.length > 0) {
          tahsilIdForVillages = fetchedTahsils[0]._id;
        }
        await fetchVillages('', tahsilIdForVillages);

        await Promise.all([fetchCircles(), fetchSubdivisions()]);
      } catch (error) {
        console.error('Error initializing region data:', error);
      }
    };

    initializeData();

    return () => {};
  }, [
    fetchTahsils,
    fetchCircles,
    fetchSubdivisions,
    fetchVillages,
    clearAllData,
  ]);

  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'token' && !e.newValue) {
        clearAllData();
      }
    };

    window.addEventListener('storage', handleStorageChange);

    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [clearAllData]);

  const value = {
    tahsils,
    circles,
    subdivisions,
    villages,
    counts,
    isLoading,
    fetchTahsils,
    fetchCircles,
    fetchSubdivisions,
    fetchVillages,
    searchTahsils,
    searchCircles,
    searchSubdivisions,
    searchVillages,
    refreshTahsils: () => fetchTahsils(''),
    refreshCircles: () => fetchCircles(''),
    refreshSubdivisions: () => fetchSubdivisions(''),
    refreshVillages: (tahsilId = '') => fetchVillages('', tahsilId),
    clearAllData,
  };

  return (
    <RegionDataContext.Provider value={value}>
      {children}
    </RegionDataContext.Provider>
  );
};
